// Реестр токенов для галереи /design. Источник значений — CSS (styles/tokens/*.css);
// здесь только имена, назначение и пары для проверки контраста. Добавил токен в CSS —
// добавь строку сюда (DESIGN.md §8), иначе он не появится в галерее (R16).

export interface ColorToken {
  /** Имя семантического токена (без namespace): `fg`, `card`, `danger-soft`. */
  name: string;
  /** Назначение одной строкой. */
  role: string;
}

export interface ColorGroup {
  title: string;
  tokens: ColorToken[];
}

export const colorGroups: ColorGroup[] = [
  {
    title: 'Поверхности',
    tokens: [
      { name: 'frame', role: 'подложка оболочки за rail и холстом' },
      { name: 'canvas', role: 'холст — окно в центре' },
      { name: 'card', role: 'вложенные карточки' },
      { name: 'elevated', role: 'меню, поповеры, модалки' },
      { name: 'muted', role: 'тихие заливки: чипы, код, шапки таблиц' },
      { name: 'hover', role: 'заливка по наведению' },
      { name: 'active', role: 'нажато / выбрано в меню' },
      { name: 'selected', role: 'выбранная строка данных' },
      { name: 'overlay', role: 'подложка под модалками' },
    ],
  },
  {
    title: 'Текст',
    tokens: [
      { name: 'fg', role: 'основной' },
      { name: 'fg-muted', role: 'вторичный' },
      { name: 'fg-subtle', role: 'третичный: мета, overline' },
      { name: 'fg-inverse', role: 'на инвертированных поверхностях' },
    ],
  },
  {
    title: 'Линии и фокус',
    tokens: [
      { name: 'line', role: 'разделители, рамки' },
      { name: 'line-strong', role: 'рамки контролов' },
      { name: 'ring', role: 'кольцо фокуса' },
    ],
  },
  {
    title: 'Бренд',
    tokens: [
      { name: 'brand', role: 'коралл: индикаторы, FAB, акценты' },
      { name: 'brand-hover', role: 'hover брендовых заливок' },
      { name: 'brand-active', role: 'нажато' },
      { name: 'brand-solid', role: 'заливка UI-элементов: тумблер, чекбокс, индикатор вкладки' },
      { name: 'brand-strong', role: 'светлый край primary-кнопки и тултипа (обе темы)' },
      { name: 'brand-deep', role: 'тёмный край primary-кнопки и тултипа' },
      { name: 'brand-soft', role: 'мягкий брендовый фон' },
      { name: 'brand-fg', role: 'брендовый текст на холсте' },
      { name: 'on-brand', role: 'текст на бренде' },
    ],
  },
  {
    title: 'Rail',
    tokens: [
      { name: 'rail', role: 'всегда тёмный' },
      { name: 'rail-fg', role: 'текст в rail' },
      { name: 'rail-fg-muted', role: 'вторичный текст в rail' },
      { name: 'rail-hover', role: 'hover в rail' },
      { name: 'rail-active', role: 'активный пункт' },
    ],
  },
  {
    title: 'Графики (порядок серий фиксирован)',
    tokens: [
      { name: 'chart-1', role: 'первая серия — коралл' },
      { name: 'chart-2', role: 'вторая — бирюза' },
      { name: 'chart-3', role: 'третья — индиго' },
      { name: 'chart-4', role: 'четвёртая — янтарь' },
    ],
  },
  {
    title: 'Стекло',
    tokens: [
      { name: 'glass', role: 'фон стекла' },
      { name: 'glass-line', role: 'рамка стекла' },
    ],
  },
];

export const tones = ['danger', 'warning', 'success', 'info', 'ai'] as const;
export type Tone = (typeof tones)[number];
export const toneLabels: Record<Tone, string> = {
  danger: 'danger — ошибка, критично',
  warning: 'warning — предупреждение',
  success: 'success — готово, ок',
  info: 'info — нейтральная информация',
  ai: 'ai — инициатива AI',
};
/** Роли тона в порядке схемы имён: solid · on · fg · soft · strong · line. */
export const toneRoles = ['', 'on-', '-fg', '-soft', '-strong', '-line'] as const;

export const severities = [0, 1, 2, 3, 4, 5] as const;
export const severityLabels = ['Not classified', 'Information', 'Warning', 'Average', 'High', 'Disaster'];

export interface ContrastPair {
  label: string;
  fg: string;
  bg: string;
  /** Поверхность под bg, если bg полупрозрачный. По умолчанию canvas. */
  base?: string;
  /** Минимум: 4.5 для текста, 3 для UI-элементов. */
  min: 4.5 | 3;
}

export const contrastPairs: ContrastPair[] = [
  { label: 'Текст на холсте', fg: 'fg', bg: 'canvas', min: 4.5 },
  { label: 'Вторичный текст на холсте', fg: 'fg-muted', bg: 'canvas', min: 4.5 },
  { label: 'Третичный текст на холсте', fg: 'fg-subtle', bg: 'canvas', min: 4.5 },
  { label: 'Текст на карточке', fg: 'fg', bg: 'card', min: 4.5 },
  { label: 'Третичный текст на карточке', fg: 'fg-subtle', bg: 'card', min: 4.5 },
  { label: 'Третичный текст на muted', fg: 'fg-subtle', bg: 'muted', base: 'canvas', min: 4.5 },
  { label: 'Брендовый текст на холсте', fg: 'brand-fg', bg: 'canvas', min: 4.5 },
  { label: 'Текст на primary-кнопке (светлый край)', fg: 'on-brand', bg: 'brand-strong', min: 4.5 },
  { label: 'Текст на primary-кнопке (тёмный край)', fg: 'on-brand', bg: 'brand-deep', min: 4.5 },
  { label: 'Кольцо фокуса на холсте', fg: 'ring', bg: 'canvas', min: 3 },
  { label: 'Заливка тумблера/чекбокса на холсте', fg: 'brand-solid', bg: 'canvas', min: 3 },
  { label: 'Линия графика (chart-1) на карточке', fg: 'chart-1', bg: 'card', min: 3 },
  { label: 'Текст в rail', fg: 'rail-fg', bg: 'rail', min: 4.5 },
  { label: 'Вторичный текст в rail', fg: 'rail-fg-muted', bg: 'rail', min: 4.5 },
  { label: 'Пластина на сцене знака', fg: 'brand-petal', bg: 'brand-night', min: 3 },
  {
    label: 'Притухшая пластина на сцене знака (веер сервисов)',
    fg: 'brand-petal-dim',
    bg: 'brand-night',
    min: 3,
  },
  ...tones.flatMap((t): ContrastPair[] => [
    { label: `${t}: текст на solid`, fg: `on-${t}`, bg: t, min: 4.5 },
    { label: `${t}: цветной текст на холсте`, fg: `${t}-fg`, bg: 'canvas', min: 4.5 },
    { label: `${t}: strong на soft`, fg: `${t}-strong`, bg: `${t}-soft`, base: 'canvas', min: 4.5 },
  ]),
  ...severities.map((n): ContrastPair => ({
    label: `sev-${n}: текст на плашке`,
    fg: `on-sev-${n}`,
    bg: `sev-${n}`,
    min: 4.5,
  })),
];

export interface TypeRole {
  utility: string;
  spec: string;
  usage: string;
}

export const typeRoles: TypeRole[] = [
  { utility: 'type-display', spec: '30/36 · 700 · tight · tabular', usage: 'hero-числа метрик' },
  { utility: 'type-title', spec: '24/32 · 600 · tight', usage: 'заголовок страницы' },
  { utility: 'type-heading', spec: '18/28 · 600 · tight', usage: 'секция' },
  { utility: 'type-subheading', spec: '14/20 · 600', usage: 'заголовок карточки, строки' },
  { utility: 'type-body', spec: '14/20 · 400', usage: 'основной текст' },
  { utility: 'type-caption', spec: '12/16 · 400', usage: 'подписи (+ text-fg-muted)' },
  {
    utility: 'type-overline',
    spec: '11/16 · 600 · uppercase · wider',
    usage: 'метки групп (+ text-fg-subtle)',
  },
];

export const textSizes = [
  'text-2xs',
  'text-xs',
  'text-sm',
  'text-base',
  'text-lg',
  'text-xl',
  'text-2xl',
  'text-3xl',
];

export const spacingSteps: { step: string; px: number; note?: string }[] = [
  { step: 'px', px: 1 },
  { step: '0.5', px: 2, note: 'подсетка' },
  { step: '1', px: 4 },
  { step: '1.5', px: 6, note: 'подсетка' },
  { step: '2', px: 8 },
  { step: '3', px: 12 },
  { step: '3.5', px: 14, note: 'иконка' },
  { step: '4', px: 16 },
  { step: '4.5', px: 18, note: 'иконка' },
  { step: '5', px: 20 },
  { step: '6', px: 24 },
  { step: '7', px: 28 },
  { step: '8', px: 32 },
  { step: '9', px: 36 },
  { step: '10', px: 40 },
  { step: '11', px: 44 },
  { step: '12', px: 48 },
  { step: '14', px: 56 },
  { step: '16', px: 64 },
  { step: '20', px: 80 },
  { step: '24', px: 96 },
];

export const radii: { utility: string; px: number; usage: string }[] = [
  { utility: 'rounded-xs', px: 4, usage: 'чекбокс, Kbd, мини-счётчик' },
  { utility: 'rounded-sm', px: 8, usage: 'пункты меню, вкладки, nav-item' },
  { utility: 'rounded-md', px: 12, usage: 'кнопки, поля, dropdown' },
  { utility: 'rounded-lg', px: 16, usage: 'карточки, холст, панели' },
  { utility: 'rounded-xl', px: 20, usage: 'модалки, drawer' },
  { utility: 'rounded-2xl', px: 24, usage: 'spotlight, поисковый док, hero' },
  { utility: 'rounded-full', px: 9999, usage: 'плашки, аватары, тултипы' },
];

export const shadows: { utility: string; usage: string }[] = [
  { utility: 'shadow-xs', usage: 'едва заметная высота' },
  { utility: 'shadow-sm', usage: 'карточка в покое' },
  { utility: 'shadow-md', usage: 'приподнятая карточка, меню' },
  { utility: 'shadow-lg', usage: 'поповеры' },
  { utility: 'shadow-xl', usage: 'модалки' },
  { utility: 'shadow-inner-card', usage: 'холст внутри рамки' },
  { utility: 'shadow-glow-brand', usage: 'hover-кольцо secondary/ghost' },
  { utility: 'shadow-brand', usage: 'primary-кнопка' },
  { utility: 'shadow-brand-lg', usage: 'primary-кнопка, hover' },
  { utility: 'shadow-lift', usage: 'приподнятая интерактивная карточка' },
  { utility: 'shadow-ai', usage: 'AI-FAB' },
];

export const layers: { utility: string; z: number; usage: string }[] = [
  { utility: 'z-base', z: 0, usage: 'поток' },
  { utility: 'z-raised', z: 10, usage: 'липкие шапки, индикаторы навигации' },
  { utility: 'z-dock', z: 20, usage: 'поисковый док' },
  { utility: 'z-fab', z: 30, usage: 'AI-FAB' },
  { utility: 'z-drawer', z: 40, usage: 'drawer, sheet' },
  { utility: 'z-overlay', z: 50, usage: 'подложка модалок' },
  { utility: 'z-modal', z: 60, usage: 'Dialog' },
  { utility: 'z-spotlight', z: 70, usage: 'AI Spotlight' },
  { utility: 'z-popover', z: 80, usage: 'dropdown, select, popover — выше модалки' },
  { utility: 'z-toast', z: 90, usage: 'тосты' },
  { utility: 'z-tooltip', z: 100, usage: 'тултипы — выше всего' },
];

export const durations = [
  { utility: 'duration-fast', ms: 120, usage: 'hover, tooltip' },
  { utility: 'duration-normal', ms: 200, usage: 'большинство состояний' },
  { utility: 'duration-smooth', ms: 320, usage: 'drawer, модалка' },
  { utility: 'duration-slow', ms: 480, usage: 'смена экрана, подъём карточки' },
];

export const easings = [
  { utility: 'ease-standard', curve: '0.4, 0, 0.2, 1' },
  { utility: 'ease-snappy', curve: '0.2, 0, 0, 1' },
  { utility: 'ease-gentle', curve: '0.4, 0, 0.6, 1' },
  { utility: 'ease-bouncy', curve: '0.34, 1.56, 0.64, 1' },
  { utility: 'ease-out-expo', curve: '0.16, 1, 0.3, 1' },
];

export const animations = [
  'animate-fade-in',
  'animate-fade-out',
  'animate-pop-in',
  'animate-pop-out',
  'animate-drop-in',
  'animate-drop-out',
  'animate-slide-in-right',
  'animate-slide-out-right',
  'animate-backdrop-in',
  'animate-backdrop-out',
  'animate-ai-pulse',
  'animate-shimmer',
];
